/**
 * Project:   bt_user_portal
 * File:      PinForm.js
 * Copyright: Safetrac Ltd
 * ====================================
 * Pin page handles all pin related actions
 * It displays after user logs in, register or
 * reset password. It is also fast track login
 * without using username/passwrord
 */

import React from 'react';
import { observer } from 'mobx-react';
import { action, extendObservable /*, useStrict */ } from 'mobx';
import store from '../../../stores/store';
import {
    PIN_CREATE_TITLE,
    PIN_CREATE_PAGE_TEXT,
    PIN_CONFIRM_TITLE,
    PIN_CONFIRM_PAGE_TEXT,
    PIN_UNLOCK_TITLE,
    PIN_UNLOCK_PAGE_TEXT,
    PIN_CONFIRM_ERROR_MESSAGE,
    PIN_BUTTON_TEXT_CREATE,
    PIN_BUTTON_TEXT_UNLOCK,
    PIN_THREE_TIMES_UNLOCK_MESSAGE,
    WARNING_MESSAGE_ON_PIN_UNLOCK_SCREEN,
	SIGNIN_REAUTHENTICATE_MSG
} from '../../../helpers/messageText';

import {
    PIN_PROCEDURE_CREATE,
    PIN_PROCEDURE_CONFIRM,
    PIN_PROCEDURE_UNLOCK,
} from '../../../helpers/constants';

const PinForm = observer(
    class PinForm extends React.Component {
        //#region CONSTRUCTOR
        /**
         * Creates an instance of PinForm.
         * @param {any} props
         */
        constructor(props) {
            super(props);

            this.form = extendObservable(this, {
                pageTitle: '',
                pageText: '',
                firstPinEntry: false,
                secondPinEntry: false,
                numberOfEnteredNumbers: 0,
                pinNumber: '',
                pinTemporaryValue: '',
                error: null,
                procedure: PIN_PROCEDURE_CREATE, //options: CREATE/CONFIRM; UNLOCK
                pinCreated: false,
                buttonText: PIN_BUTTON_TEXT_CREATE,
                numberOfLogins: 0,

                set: action((field, value) => {
                    this[field] = value;
                }),

                /**
                 * It handles different states/options
                 * in the pin usage: create and validate
                 * or unlock protected areas.
                 */
                setPin: action(number => {
                    if (this.pinNumber.length <= 6) {
                        this.pinNumber = this.pinNumber + number.toString();
                    }

                    if (this.pinNumber.length === 6) {
                        this.set('error', null);
                        if (this.procedure === PIN_PROCEDURE_CREATE) {
                            // CREATE PIN FIRST GO
                            this.set('pinTemporaryValue', this.pinNumber);
                            this.set('pinNumber', '');
                            this.set('procedure', PIN_PROCEDURE_CONFIRM);
                            this.form.set('pageTitle', PIN_CONFIRM_TITLE);
                            this.set('pageText', PIN_CONFIRM_PAGE_TEXT);
                            this.set('numberOfEnteredNumbers', 0);
                        } else if (this.procedure === PIN_PROCEDURE_CONFIRM) {
                            // CREATE PIN SECOND GO
                            // check if pin entered second time match the first pin
                            if (this.pinNumber === this.pinTemporaryValue) {
                                this.handleSubmit();
                            } else {
                                this.set('pinNumber', '');
                                this.set('procedure', PIN_PROCEDURE_CREATE);
                                this.set('pageText', PIN_CREATE_PAGE_TEXT);
                                this.set('error', PIN_CONFIRM_ERROR_MESSAGE);
                                this.set('numberOfEnteredNumbers', 0);
                            }
                        } else if (this.procedure === PIN_PROCEDURE_UNLOCK) {
                            // UNLOCK PIN
                            this.handleSubmit();
                        }
                    }
                }),

                /**
                 * It handles submit procedure
                 * depending of the pin state:
                 * creation of a pin or validation
                 * and unlock protected areas
                 */
                handleSubmit: action(async () => {
                    if (this.procedure === PIN_PROCEDURE_CONFIRM) {
                        // call func in the UserStore and if success redirect
                        // to the success message. if not ??? and then
                        store.user.setPinValue(this.pinNumber).then(hashedPin => {
                            this.form.set('pinCreated', true);
                            setTimeout(function() {
                                store.user.setPinExists(true);
                                store.user.setPinValidated(true);
                            }, 3000);
                        });
                    }

                    if (this.procedure === PIN_PROCEDURE_UNLOCK) {
                        let isPinValid = await store.user.comparePinWithValueInStorage(
                            this.pinNumber,
                        );

                        if (isPinValid) {
                            let refreshToken = await store.user.refreshBearerToken();

                            if (refreshToken === false) {
                                // this.props.history.push('/error');

								//IHT: BT-1433, if refresh token fails, send user to reauthenticate
								this.props.history.push(`/login?${SIGNIN_REAUTHENTICATE_MSG}`);
                            } else {
                                store.user.setPinValidated(true);
                                // let setTempDocKey = await store.documents.setTempElectronDocumentKey();
                                await store.documents.setTempElectronDocumentKey();
                                this.props.history.push('/dashboard');
                            }
                        } else {
                            this.set('pinNumber', '');
                            this.set('numberOfEnteredNumbers', 0);
                            this.form.set('error', PIN_CONFIRM_ERROR_MESSAGE);
                            this.set('numberOfLogins', this.numberOfLogins + 1);
                            switch (this.numberOfLogins) {
                                case 0:
                                case 1:
                                    break;
                                case 2:
                                    const message =
                                        PIN_CONFIRM_ERROR_MESSAGE +
                                        ' ' +
                                        PIN_THREE_TIMES_UNLOCK_MESSAGE;
                                    this.form.set('error', message);
                                    break;
                                case 3:
                                case 4:
                                    // store.user.logout();  //IHT - don't remove data, just make them re-authenticate
									store.user.askToReauthenticate();
                                    this.props.history.push('/login');
                                    break;
                                default:
                                    break;
                            }
                        }
                    }
                }),
            });
        }
        //#endregion

        /**
         * increases number of entered numbers
         * in the var called numberOfEnteredNumbers
         */
        increaseNumberOfEnteredNumber = () => {
            if (this.form.numberOfEnteredNumbers <= 6) {
                let currentNumberOfNumbers = this.form.numberOfEnteredNumbers + 1;
                this.form.set('numberOfEnteredNumbers', currentNumberOfNumbers);
            }
        };

        /**
         * Action on click
         * 'Return to Sign in'
         */
        onReturnHandler = () => {
            store.user.deletePinAndOfflineKeyFromStorage().then(() => {
                store.user.setPinValidated(false);
                store.user.setPinExists(false);
                store.user.setLoggedIn(false);
                this.props.history.push('/login');
            });
        };

        /**
         * Build in function used
         * to check if pin exists and is validated
         */
        componentWillMount() {
            if (window.offlineApiExtension) {
                // SIGNIN PAGE USED
                // COVER REGISTRATION, RESET PASSWORD
                // AND REGULAR LOGIN PAGE USAGE

                if (!store.user.loggedIn) {
                    this.props.history.push('/login');
                } else {
                    this.isHashedPinExists().then(pinExists => {
                        if (store.user.loggedIn && pinExists && !store.user.pinValidated) {
                            this.form.set('pageTitle', PIN_UNLOCK_TITLE);
                            this.form.set('pageText', PIN_UNLOCK_PAGE_TEXT);
                            this.form.set('procedure', PIN_PROCEDURE_UNLOCK);
                            this.form.set('buttonText', PIN_BUTTON_TEXT_UNLOCK);
                        } else if (store.user.loggedIn && !pinExists && !store.user.pinValidated) {
                            this.form.set('pageTitle', PIN_CREATE_TITLE);
                            this.form.set('pageText', PIN_CREATE_PAGE_TEXT);
                        }
                    });
                }
            } else {
                store.user.logout();
            }
        }

        async isHashedPinExists() {
            let hashedPin = await store.user.getValueFromActorStorage('hashedPin');
            return hashedPin;
        }

        /**
         * This func is handli9ng
         * every number click
         */
        handleNumberClick = e => {
            if (this.form.numberOfEnteredNumbers < 7) {
                this.increaseNumberOfEnteredNumber();
            }

            this.form.setPin(e);
        };

        /**
         * This func is handling
         * the clear button click
         */
        handleCleanNumbersClick = () => {
            this.form.set('numberOfEnteredNumbers', 0);
            this.set('pinNumber', '');
        };
        //#region PAGE AND RENDER METHODS
        /**
         * Pin screen content
         * depending on the user's current state
         */
        pinScreen = () => {
            if (this.form.pinCreated) {
                return (
                    <div className="mt-3">
                        <p className="alert alert-success mt-4 text-center" role="alert">
                        <i className="fa fa-check-circle fa-3x"></i><br />
                            Success
                        </p>
                    </div>
                );
            } else {
                return (
                    <div>
                        <div className="mt-3">
                            <legend>
                                <span className="legend-border-bottom">{this.form.pageTitle}</span>
                            </legend>
                            <hr />
                            {this.form.error !== null && (
                                <div className="alert alert-danger mt-4" role="alert">
                                    {this.form.error === ''
                                        ? PIN_CONFIRM_ERROR_MESSAGE
                                        : this.form.error}
                                </div>
                            )}
                            <p>{this.form.pageText}</p>
                            <form>
                                <div className="">
                                    <div className="row justify-content-center">
                                        <button
                                            type="button"
                                            className={
                                                'btn btn-outline-secondary pincode-circle ' +
                                                (this.form.numberOfEnteredNumbers > 0
                                                    ? 'pincode-fill'
                                                    : '')
                                            }
                                        />
                                        <button
                                            type="button"
                                            className={
                                                'btn btn-outline-secondary pincode-circle ' +
                                                (this.form.numberOfEnteredNumbers > 1
                                                    ? 'pincode-fill'
                                                    : '')
                                            }
                                        />
                                        <button
                                            type="button"
                                            className={
                                                'btn btn-outline-secondary pincode-circle ' +
                                                (this.form.numberOfEnteredNumbers > 2
                                                    ? 'pincode-fill'
                                                    : '')
                                            }
                                        />
                                        <button
                                            type="button"
                                            className={
                                                'btn btn-outline-secondary pincode-circle ' +
                                                (this.form.numberOfEnteredNumbers > 3
                                                    ? 'pincode-fill'
                                                    : '')
                                            }
                                        />
                                        <button
                                            type="button"
                                            className={
                                                'btn btn-outline-secondary pincode-circle ' +
                                                (this.form.numberOfEnteredNumbers > 4
                                                    ? 'pincode-fill'
                                                    : '')
                                            }
                                        />
                                        <button
                                            type="button"
                                            className={
                                                'btn btn-outline-secondary pincode-circle ' +
                                                (this.form.numberOfEnteredNumbers > 5
                                                    ? 'pincode-fill'
                                                    : '')
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="">
                                    <div className="row justify-content-center">
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 1)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            1
                                        </button>
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 2)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            2
                                        </button>
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 3)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            3
                                        </button>
                                    </div>
                                    <div className="row justify-content-center">
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 4)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            4
                                        </button>
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 5)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            5
                                        </button>
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 6)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            6
                                        </button>
                                    </div>
                                    <div className="row justify-content-center">
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 7)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            7
                                        </button>
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 8)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            8
                                        </button>
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 9)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            9
                                        </button>
                                    </div>
                                    <div className="row justify-content-center">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary keypad keypad-hidden"
                                        />
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleNumberClick.bind(this, 0)}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            0
                                        </button>
                                        <button
                                            disabled={this.form.numberOfEnteredNumbers === 6}
                                            onClick={this.handleCleanNumbersClick}
                                            type="button"
                                            className="btn btn-outline-secondary keypad"
                                        >
                                            <i className="fa fa-times" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="">
                            <hr />
                            <div className="row justify-content-center pl-4 pr-3 pb-3">
                                <a
                                    onClick={this.onReturnHandler}
                                    className="col-sm-6 btn btn-outline-primary no-outline"
                                >
                                    {this.form.buttonText}
                                </a>
                            </div>
                            {this.form.procedure === PIN_PROCEDURE_UNLOCK && (
                                <div className="alert alert-info" role="alert">
                                    {WARNING_MESSAGE_ON_PIN_UNLOCK_SCREEN}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        };

        /**
         * Render page fun calling pinScreen func
         * @param {any} props
         */
        renderPage = props => {
            return this.pinScreen();
        };

        /**
         * Page main render func
         * @returns {string} page
         */
        render() {
            return this.renderPage(this.props);
        }
        //#endregion
    },
);

export default PinForm;



// WEBPACK FOOTER //
// ./src/containers/PublicArea/components/PinForm.js